/**
 * Reusable fullscreen Mapbox map modal (web only).
 * Used by VenueScreen and PostGridDisplay (and elsewhere) for a consistent map experience.
 */
import React, { useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, Modal, Platform } from 'react-native';
import { venueStyles } from '../styles/venueStyles';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';

let mapboxgl = null;
if (Platform.OS === 'web') {
  try {
    // eslint-disable-next-line global-require
    mapboxgl = require('mapbox-gl');
    require('mapbox-gl/dist/mapbox-gl.css');
  } catch (e) {
    // mapbox-gl not available
  }
}

export function FullscreenMapModal({
  visible,
  onClose,
  center,
  label,
  mapStyle = 'streets-v11',
  onMapStyleChange,
  showTourPlannerButton = false,
  onTourPlannerPress,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !mapboxgl || !MAPBOX_ACCESS_TOKEN || !visible || !center || !Array.isArray(center)) return;

    const id = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
      mapRef.current = new mapboxgl.Map({
        container: el,
        style: `mapbox://styles/mapbox/${mapStyle}`,
        center,
        zoom: 14,
        maxZoom: 22,
        trackResize: true,
      });
      const map = mapRef.current;
      map.on('load', () => {
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        new mapboxgl.Marker().setLngLat(center).addTo(map);
      });
    }, 50);

    return () => {
      clearTimeout(id);
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {
          // ignore
        }
        mapRef.current = null;
      }
    };
  }, [visible, center, mapStyle]);

  if (Platform.OS !== 'web') return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={venueStyles.mapFullscreenOverlay}>
        <View style={venueStyles.mapFullscreenHeader}>
          <View style={venueStyles.mapFullscreenHeaderLeft}>
            <TouchableOpacity
              style={venueStyles.mapFullscreenActionBtn}
              onPress={onClose}
              activeOpacity={0.8}
            >
              <Text style={venueStyles.mapFullscreenActionBtnText}>Go Back</Text>
            </TouchableOpacity>
            {showTourPlannerButton && onTourPlannerPress && (
              <TouchableOpacity
                style={venueStyles.mapFullscreenActionBtn}
                onPress={onTourPlannerPress}
                activeOpacity={0.8}
              >
                <Text style={venueStyles.mapFullscreenActionBtnText}>Go to Tour Planner</Text>
              </TouchableOpacity>
            )}
          </View>
          {onMapStyleChange && (
            <TouchableOpacity
              style={venueStyles.mapStyleToggleBtn}
              onPress={() => onMapStyleChange(mapStyle === 'streets-v11' ? 'dark-v11' : 'streets-v11')}
            >
              <Text style={venueStyles.mapStyleToggleText}>
                {mapStyle === 'streets-v11' ? 'Dark map' : 'Streets'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
        <View style={venueStyles.mapFullscreenContent}>
          <div
            ref={containerRef}
            style={{ width: '100%', height: '100%', minHeight: 400 }}
            aria-label={label || 'Fullscreen map'}
          />
        </View>
      </View>
    </Modal>
  );
}
